// Arrow and dot controls for the featured-products track. The track itself is a
// plain CSS scroll-snap row, so this only reports overflow and drives scrollBy.
class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prev = this.querySelector("[data-carousel-prev]");
    this.next = this.querySelector("[data-carousel-next]");
    this.dots = this.querySelector("[data-carousel-dots]");
    if (!this.track) return;

    // Each product_card block also emits <link>/<script> tags into the track;
    // only the cards take part in the layout and the loop.
    this.cards = [...this.track.children].filter(
      (child) => !child.matches("link, script, style"),
    );
    this.onScroll = this.onScroll.bind(this);
    this.onSettle = this.onSettle.bind(this);
    this.sync = this.sync.bind(this);

    this.prev?.addEventListener("click", () => this.step(-1));
    this.next?.addEventListener("click", () => this.step(1));
    this.dots?.addEventListener("click", (event) => {
      const dot = event.target.closest("button");
      if (dot) this.goTo(Number(dot.dataset.index));
    });
    this.track.addEventListener("scroll", this.onScroll, { passive: true });
    if ("onscrollend" in window) {
      this.track.addEventListener("scrollend", this.onSettle);
    }

    this.observer = new ResizeObserver(this.sync);
    this.observer.observe(this.track);
    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.track?.removeEventListener("scroll", this.onScroll);
    this.track?.removeEventListener("scrollend", this.onSettle);
    clearTimeout(this.settleTimer);
  }

  // One card plus the gap between cards, read off the live layout so the
  // desktop/tablet/mobile card widths never have to be duplicated in JS. The
  // computed width ignores a hover scale, which getBoundingClientRect would not.
  get stride() {
    const card = this.cards[0];
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return parseFloat(getComputedStyle(card).width) + gap;
  }

  get cardOrder() {
    return [...this.track.children].filter((child) =>
      this.cards.includes(child),
    );
  }

  get max() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  get looping() {
    return this.hasAttribute("data-loop") && this.max > 4;
  }

  // Loop works like Swiper's: a card is moved from one end of the row to the
  // other and the scroll offset is shifted by one stride, so nothing moves on
  // screen but there is always another card to scroll towards.
  rotate(direction) {
    const stride = this.stride;
    const left = this.track.scrollLeft;
    const order = this.cardOrder;
    if (direction > 0) {
      this.track.append(order[0]);
      this.track.scrollTo({ left: left - stride, behavior: "instant" });
    } else {
      this.track.prepend(order[order.length - 1]);
      this.track.scrollTo({ left: left + stride, behavior: "instant" });
    }
  }

  // Keeps a card of room on both sides after a swipe or arrow scroll settles.
  // Skipped when the row overflows by less than two cards, where making room
  // on one side would pin the other edge and rotate back on the next settle.
  makeRoom() {
    if (!this.looping || this.max < this.stride * 2 - 2) return;
    const left = this.track.scrollLeft;
    if (left <= 2) this.rotate(-1);
    else if (left >= this.max - 2) this.rotate(1);
  }

  step(direction) {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const stride = this.stride;
    const left = this.track.scrollLeft;

    if (this.looping) {
      if (direction > 0 && left + stride > this.max + 2) this.rotate(1);
      if (direction < 0 && left - stride < -2) this.rotate(-1);
    }
    this.track.scrollBy({
      left: stride * direction,
      behavior: reduced ? "auto" : "smooth",
    });
  }

  // Distance from the current scroll position to a card's snap point. Desktop
  // snaps card starts to the scroll padding, mobile snaps card centres.
  offsetOf(card) {
    const track = this.track.getBoundingClientRect();
    const rect = card.getBoundingClientRect();
    if (getComputedStyle(card).scrollSnapAlign.includes("center")) {
      return rect.left + rect.width / 2 - (track.left + track.width / 2);
    }
    const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    return rect.left - track.left - padding;
  }

  // One dot per reachable snap point: every card when looping or centring,
  // otherwise only the cards that can sit first in the row.
  get stops() {
    const count = this.cards.length;
    if (this.looping || !this.cards[0]) return count;
    if (getComputedStyle(this.cards[0]).scrollSnapAlign.includes("center")) {
      return count;
    }
    // Rounds up so a partial last position still gets a dot; the slack keeps
    // sub-pixel overflow from adding one.
    return Math.min(Math.ceil(this.max / this.stride - 0.05) + 1, count);
  }

  goTo(index) {
    const card = this.cards[index];
    if (!card) return;
    if (this.looping) {
      for (let i = 0; i < this.cards.length; i++) {
        const target = this.track.scrollLeft + this.offsetOf(card);
        if (target > this.max + 2) this.rotate(1);
        else if (target < -2) this.rotate(-1);
        else break;
      }
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    this.track.scrollBy({
      left: this.offsetOf(card),
      behavior: reduced ? "auto" : "smooth",
    });
  }

  syncDots(overflow) {
    if (!this.dots) return;
    const count = overflow ? this.stops : 0;
    if (this.dots.children.length !== count) {
      const label = this.dots.dataset.dotLabel || "[n]";
      this.dots.replaceChildren(
        ...Array.from({ length: count }, (_, i) => {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className = "swiper-pagination-bullet";
          dot.dataset.index = i;
          dot.setAttribute("aria-label", label.replace("[n]", i + 1));
          return dot;
        }),
      );
    }
    if (!count) return;

    let active = 0;
    if (!this.looping && this.track.scrollLeft >= this.max - 2) {
      active = count - 1;
    } else {
      let nearest = Infinity;
      this.cards.forEach((card, i) => {
        const distance = Math.abs(this.offsetOf(card));
        if (distance < nearest) {
          nearest = distance;
          active = i;
        }
      });
      active = Math.min(active, count - 1);
    }
    [...this.dots.children].forEach((dot, i) => {
      const current = i === active;
      dot.classList.toggle("swiper-pagination-bullet-active", current);
      if (current) dot.setAttribute("aria-current", "true");
      else dot.removeAttribute("aria-current");
    });
  }

  onScroll() {
    if (!("onscrollend" in window)) {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 150);
    }
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.sync();
    });
  }

  onSettle() {
    this.makeRoom();
    this.sync();
  }

  sync() {
    const { scrollLeft } = this.track;
    const max = this.max;
    // A few px of slack: flex-basis rounding on an exactly-fitting row can leave
    // a sub-pixel overflow, which would show the arrows on the desktop layout.
    const overflow = max > 4;
    const wasOverflowing = this.hasAttribute("data-overflow");
    this.toggleAttribute("data-overflow", overflow);

    // A resize into a layout that fits (desktop row, mobile grid) puts the
    // cards back in their merchant-set order.
    if (!overflow && this.cardOrder[0] !== this.cards[0]) {
      this.track.append(...this.cards);
    }
    if (overflow && !wasOverflowing) this.makeRoom();

    const loop = this.hasAttribute("data-loop");
    if (this.prev) this.prev.disabled = !overflow || (!loop && scrollLeft <= 2);
    if (this.next) {
      this.next.disabled = !overflow || (!loop && scrollLeft >= max - 2);
    }
    this.syncDots(overflow);
  }
}

if (!customElements.get("product-carousel")) {
  customElements.define("product-carousel", ProductCarousel);
}
